import React, { useState, useEffect } from "react";
import LinkedInPreviewCard from "./LinkedInPreviewCard";
import { ComplianceChecker, PLUG_TYPES, PLUG_TONES, PLUG_QUESTIONS_MAP, QUICK_CAMPAIGNS, ADVANCED_OVERRIDES } from "../common/SharedComponents";
import OverrideConfirmation from "./OverrideConfirmation";

export default
function PlugPostPanel({ user, dashState, API_BASE, isTrialing, trialDaysRemaining, skipToShameless, onClearSkip, onPageChange }) {
  const G = "#0077B5", GRN = "#1D9E75", CARD = "#FFFFFF", B = "#E1E9EE";
  const plan = (user?.plan || "").toLowerCase();
  const isPremium = plan === "authority" || plan === "authority_pro" || plan === "team" || plan === "agency" || plan === "authority_team" || plan === "authority_team_member" || plan === "pro_team" || plan === "pro_team_member" || isTrialing;
  const isWorkspaceMemberOnly = user?.is_workspace_member && !user?.is_workspace_admin;
  const isWorkspaceAdmin = !!user?.is_workspace_admin;
  const hasPlugConfigured = dashState?.has_plug_configured;

  // mode: "home" = two-section overview, "evergreen" = shameless wizard, "campaign" = campaign wizard
  const [mode, setMode] = useState(skipToShameless ? "evergreen" : "home");
  const [step, setStep] = useState(skipToShameless ? 2 : 2); // wizard starts at tone (step 2)
  const [plugType, setPlugType] = useState("shameless");

  // Clear the skip flag so navigating back to plug page later starts at home
  useEffect(() => { if (skipToShameless && onClearSkip) onClearSkip(); }, []); // eslint-disable-line

  const [tone, setTone] = useState("Professional");
  const [answers, setAnswers] = useState({});
  const [offeringLink, setOfferingLink] = useState("");
  const [offering, setOffering] = useState("");
  const [generating, setGenerating] = useState(false);
  const [caption, setCaption] = useState("");
  const [saveDefault, setSaveDefault] = useState(false);
  const [history, setHistory] = useState([]);
  const [, setHistLoading] = useState(false); // eslint-disable-line no-unused-vars
  const [actionMsg, setActionMsg] = useState("");
  const [scheduling, setScheduling] = useState(false);
  const [scheduleTime, setScheduleTime] = useState("");
  const [currentPlugId, setCurrentPlugId] = useState(null);
  const [complianceResult, setComplianceResult] = useState(null);
  const [, setComplianceAck] = useState({}); // eslint-disable-line no-unused-vars
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [repeatMode, setRepeatMode] = useState("once"); // once | weekly | monthly | custom
  const [repeatCount, setRepeatCount] = useState(4);
  const [repeatScheduling, setRepeatScheduling] = useState(false);
  const [voiceDirection, setVoiceDirection] = useState("staff"); // staff | ambassador
  const [improving, setImproving] = useState({}); // { fieldKey: true }
  const [suggestions, setSuggestions] = useState({}); // { fieldKey: "improved text" }

  // Load history on mount
  useEffect(() => {
    if (!isPremium || isWorkspaceMemberOnly || isWorkspaceAdmin) return;
    setHistLoading(true);
    fetch(`${API_BASE}/app/api/user/plug/list`, { credentials: "include" })
      .then(r => r.ok ? r.json() : { plug_posts: [] })
      .then(d => setHistory(d.plug_posts || []))
      .catch(() => {})
      .finally(() => setHistLoading(false));
  }, []); // eslint-disable-line

  const questions = PLUG_QUESTIONS_MAP[plugType] || [];

  const handleImprove = async (fieldKey, question, answer) => {
    if (!answer || answer.trim().length < 10) return;
    setImproving(s => ({ ...s, [fieldKey]: true }));
    try {
      const r = await fetch(`${API_BASE}/app/api/user/plug/improve-answer`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question, answer: answer.trim(), tone: tone.toLowerCase(), voice: voiceDirection }),
      });
      const d = await r.json();
      if (d.improved) setSuggestions(s => ({ ...s, [fieldKey]: d.improved }));
    } catch { /* handled */ }
    setImproving(s => ({ ...s, [fieldKey]: false }));
  };

  const acceptSuggestion = (fieldKey, question) => {
    const text = suggestions[fieldKey];
    if (!text) return;
    if (fieldKey === "offering") setOffering(text);
    else setAnswers(a => ({ ...a, [question]: text }));
    setSuggestions(s => { const n = { ...s }; delete n[fieldKey]; return n; });
  };

  const dismissSuggestion = (fieldKey) => {
    setSuggestions(s => { const n = { ...s }; delete n[fieldKey]; return n; });
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setCaption("");
    setActionMsg("");
    try {
      const r = await fetch(`${API_BASE}/app/api/user/plug/generate`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plug_type: plugType, tone: tone.toLowerCase(), offering, offering_link: offeringLink || null, answers }),
      });
      const d = await r.json();
      if (d.caption) {
        setCaption(d.caption);
        const cr = await fetch(`${API_BASE}/app/api/user/plug/create`, {
          method: "POST", credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ plug_type: plugType, tone: tone.toLowerCase(), offering, offering_link: offeringLink || null, answers, generated_caption: d.caption }),
        });
        const cd = await cr.json();
        if (cd.plug_post) setCurrentPlugId(cd.plug_post.id);
        setStep(5);
      } else {
        setActionMsg(d.error || "Generation failed — try again.");
        setStep(3);
      }
    } catch (e) {
      setActionMsg("Generation failed — try again.");
      setStep(3);
    }
    setGenerating(false);
  };

  const handleSaveDraft = async () => {
    if (!currentPlugId) return;
    await fetch(`${API_BASE}/app/api/user/plug/${currentPlugId}/update`, {
      method: "PUT", credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ generated_caption: caption }),
    });
    if (saveDefault) {
      await fetch(`${API_BASE}/app/api/user/plug/save-defaults`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ default_plug_type: plugType, default_plug_tone: tone.toLowerCase(), default_offering: offering, default_offering_link: offeringLink || null, default_plug_answers: answers }),
      });
    }
    setActionMsg("Saved as draft.");
    setHistory(h => [{ id: currentPlugId, plug_type: plugType, generated_caption: caption, status: "draft", created_at: new Date().toISOString() }, ...h]);
  };

  const getRepeatDates = () => {
    if (!scheduleTime) return [];
    const base = new Date(scheduleTime);
    if (repeatMode === "once") return [base];
    const dates = [base];
    for (let i = 1; i < repeatCount; i++) {
      const d = new Date(base);
      if (repeatMode === "weekly") d.setDate(d.getDate() + i * 7);
      else if (repeatMode === "monthly") d.setMonth(d.getMonth() + i);
      dates.push(d);
    }
    return dates;
  };

  const handleSchedule = async () => {
    if (!scheduleTime || !currentPlugId) return;
    setScheduling(true);
    const dates = getRepeatDates();

    if (dates.length === 1) {
      try {
        const r = await fetch(`${API_BASE}/app/api/user/plug/${currentPlugId}/schedule`, {
          method: "POST", credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ scheduled_time: dates[0].toISOString() }),
        });
        const d = await r.json();
        if (d.ok) setActionMsg(`Scheduled for ${dates[0].toLocaleString()}`);
        else setActionMsg(d.error || "Schedule failed.");
      } catch { setActionMsg("Schedule failed."); }
    } else {
      setRepeatScheduling(true);
      let successCount = 0;
      try {
        const r1 = await fetch(`${API_BASE}/app/api/user/plug/${currentPlugId}/schedule`, {
          method: "POST", credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ scheduled_time: dates[0].toISOString() }),
        });
        if ((await r1.json()).ok) successCount++;

        for (let i = 1; i < dates.length; i++) {
          const cr = await fetch(`${API_BASE}/app/api/user/plug/create`, {
            method: "POST", credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ plug_type: plugType, tone: tone.toLowerCase(), offering, offering_link: offeringLink || null, answers, generated_caption: caption }),
          });
          const cd = await cr.json();
          if (cd.plug_post) {
            const sr = await fetch(`${API_BASE}/app/api/user/plug/${cd.plug_post.id}/schedule`, {
              method: "POST", credentials: "include",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ scheduled_time: dates[i].toISOString() }),
            });
            if ((await sr.json()).ok) successCount++;
          }
        }
        setActionMsg(`Scheduled ${successCount} of ${dates.length} campaign posts.`);
      } catch { setActionMsg("Some campaign posts failed to schedule."); }
      setRepeatScheduling(false);
    }
    setScheduling(false);
  };

  const handlePostNow = async () => {
    if (!currentPlugId) return;
    const fiveMin = new Date(Date.now() + 5 * 60 * 1000).toISOString();
    const r = await fetch(`${API_BASE}/app/api/user/plug/${currentPlugId}/schedule`, {
      method: "POST", credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ scheduled_time: fiveMin }),
    });
    const d = await r.json();
    setActionMsg(d.ok ? "Queued to post in ~5 minutes." : (d.error || "Failed."));
  };

  const handleMute = async (id) => {
    await fetch(`${API_BASE}/app/api/user/plug/${id}/mute`, { method: "PUT", credentials: "include" });
    setHistory(h => h.map(p => p.id === id ? { ...p, status: "muted" } : p));
    if (id === currentPlugId) setActionMsg("Plug muted for this week.");
  };

  const handleDelete = async (id) => {
    const r = await fetch(`${API_BASE}/app/api/user/plug/${id}/delete`, { method: "DELETE", credentials: "include" });
    if (r.ok) setHistory(h => h.filter(p => p.id !== id));
  };

  const STATUS_COLORS = { draft: "#888", scheduled: GRN, posted: G, muted: "#F59E0B", deleted: "#dc3545" };

  // Filter out shameless from campaign grids — it's now the evergreen plug
  const campaignOverrides = ADVANCED_OVERRIDES.filter(o => o.id !== "shameless");

  const handleBackToHome = () => {
    setMode("home");
    setStep(2);
    setPlugType("shameless");
    setCaption("");
    setActionMsg("");
    setCurrentPlugId(null);
    setScheduling(false);
    setShowConfirmation(false);
  };

  // Workspace admins use workspace plug system, not personal plug
  if (isWorkspaceAdmin) {
    return (
      <div style={{ maxWidth: 480, margin: "60px auto", padding: 32, textAlign: "center", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
        <div style={{ fontSize: 36, marginBottom: 16 }}>&#127919;</div>
        <h3 style={{ fontSize: 20, fontWeight: 600, marginBottom: 12 }}>Team Campaigns</h3>
        <p style={{ fontSize: 15, color: "#666", lineHeight: 1.6, marginBottom: 24 }}>
          As a workspace admin, your team's Authority Presence and campaigns are managed in Workspace Settings.
        </p>
        <button onClick={() => { window.location.hash = "/team"; if (onPageChange) onPageChange("team"); }} style={{ display: "inline-block", padding: "12px 24px", fontSize: 15, fontWeight: 600, backgroundColor: "#0077B5", color: "white", border: "none", borderRadius: 8, textDecoration: "none", cursor: "pointer" }}>
          Go to workspace dashboard
        </button>
      </div>
    );
  }

  // ── Workspace member: team-managed state ──
  if (isWorkspaceMemberOnly) {
    return (
      <div style={{ background: CARD, border: `1px solid ${B}`, borderRadius: 14, padding: "40px 32px", textAlign: "center" }}>
        <div style={{ fontSize: 40, marginBottom: 16 }}>&#128029;</div>
        <div style={{ fontFamily: "'Playfair Display',Georgia,serif", fontSize: 24, marginBottom: 12 }}>
          Your content is managed by your team admin.
        </div>
        <div style={{ fontSize: 15, color: "#666", lineHeight: 1.7, marginBottom: 28, maxWidth: 480, margin: "0 auto 28px" }}>
          You can personalize your voice and approve your content from your workspace dashboard.
        </div>
        <button onClick={() => { window.location.hash = "/team"; if (onPageChange) onPageChange("team"); }} style={{ display: "inline-block", padding: "14px 32px", fontFamily: "'Playfair Display',Georgia,serif", fontSize: 16, backgroundColor: G, color: "#fff", border: "none", borderRadius: 10, textDecoration: "none", cursor: "pointer" }}>Go to Workspace →</button>
      </div>
    );
  }

  // ── Not premium: locked state ──────────────────────────────────────────────
  if (!isPremium) {
    return (
      <div style={{ background: CARD, border: `1px solid ${B}`, borderRadius: 14, padding: "40px 32px", textAlign: "center" }}>
        <div style={{ fontSize: 40, marginBottom: 16 }}>&#128274;</div>
        <div style={{ fontFamily: "'Playfair Display',Georgia,serif", fontSize: 24, marginBottom: 12 }}>
          My Plug is an <em style={{ color: G }}>Authority feature.</em>
        </div>
        <div style={{ fontSize: 15, color: "#666", lineHeight: 1.7, maxWidth: 480, margin: "0 auto 28px" }}>
          Upgrade to Authority to unlock your evergreen plug and campaign overrides — targeted posts written in your voice, scheduled automatically.
        </div>
        <button onClick={() => { window.location.hash = "/settings/billing"; }} style={{ background: G, color: "#fff", border: "none", borderRadius: 10, padding: "14px 32px", fontFamily: "'Playfair Display',Georgia,serif", fontSize: 16, cursor: "pointer" }}>
          Upgrade →
        </button>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════
  // HOME: Two-section overview
  // ══════════════════════════════════════════════════════════════
  if (mode === "home") {
    return (
      <div>
        <div style={{ marginBottom: 32 }}>
          <div style={{ fontFamily: "'Playfair Display',Georgia,serif", fontSize: 26, marginBottom: 4 }}>My Plug</div>
          <div style={{ fontSize: 15, color: "#666", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>Your weekly self-promotion, powered by AI.</div>
        </div>

        {/* ── SECTION 1: EVERGREEN PLUG ── */}
        <div style={{ background: CARD, border: `1px solid ${B}`, borderRadius: 14, padding: "28px 28px", marginBottom: 32 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <span style={{ fontSize: 22 }}>&#128293;</span>
            <div style={{ fontFamily: "'Playfair Display',Georgia,serif", fontSize: 20 }}>Your Evergreen Plug</div>
            {hasPlugConfigured && <span style={{ background: GRN + "20", color: GRN, border: `1px solid ${GRN}40`, borderRadius: 6, fontSize: 11, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontWeight: 700, padding: "3px 10px" }}>ACTIVE</span>}
          </div>
          <div style={{ fontSize: 14, color: "#666", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", lineHeight: 1.7, marginBottom: 20, maxWidth: 520 }}>
            This is your always-on plug post. It runs every week automatically, telling your audience what you do and how to reach you. Set it up once — it keeps working for you.
          </div>

          {hasPlugConfigured ? (
            <div>
              {dashState?.default_offering && (
                <div style={{ background: "#F9FAFB", border: `1px solid ${B}`, borderRadius: 8, padding: "12px 16px", marginBottom: 16, fontSize: 14, color: "#444", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
                  <div style={{ fontSize: 11, color: "#999", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6, fontWeight: 600 }}>What you offer</div>
                  {dashState.default_offering}
                </div>
              )}
              <button
                onClick={() => { setMode("evergreen"); setPlugType("shameless"); setStep(2); }}
                style={{ background: G, color: "#fff", border: "none", borderRadius: 10, padding: "12px 24px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 14, fontWeight: 600, cursor: "pointer" }}
              >Edit evergreen plug →</button>
            </div>
          ) : (
            <button
              onClick={() => { setMode("evergreen"); setPlugType("shameless"); setStep(2); }}
              style={{ background: G, color: "#fff", border: "none", borderRadius: 10, padding: "13px 28px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 15, fontWeight: 600, cursor: "pointer" }}
            >Set up your evergreen plug →</button>
          )}
        </div>

        {/* ── SECTION 2: CAMPAIGN OVERRIDES ── */}
        <div style={{ marginBottom: 32 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <span style={{ fontSize: 20 }}>&#127919;</span>
            <div style={{ fontFamily: "'Playfair Display',Georgia,serif", fontSize: 20 }}>Campaign Overrides</div>
            <span style={{ background: "#FEF3C7", color: "#92400E", border: "1px solid #FCD34D", borderRadius: 6, fontSize: 11, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontWeight: 700, padding: "3px 10px", letterSpacing: "0.08em" }}>AUTHORITY EXCLUSIVE</span>
          </div>
          <div style={{ fontSize: 14, color: "#666", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", lineHeight: 1.7, marginBottom: 20, maxWidth: 520 }}>
            Replace your evergreen plug for a specific week with something targeted — a win, a hot take, a referral nudge.
          </div>

          <div style={{ fontSize: 11, color: "#999", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 12, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>Quick Campaigns</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 12, marginBottom: 24 }}>
            {QUICK_CAMPAIGNS.map(pt => (
              <div key={pt.id} onClick={() => { if (!pt.disabled) { setPlugType(pt.id); setMode("campaign"); setStep(pt.id === "custom" ? 3 : 2); } }} style={{ background: pt.disabled ? "#F9FAFB" : CARD, border: `1px solid ${B}`, borderRadius: 10, padding: "16px 18px", cursor: pt.disabled ? "default" : "pointer", transition: "all 0.15s", opacity: pt.disabled ? 0.5 : 1 }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: pt.disabled ? "#AAA" : "#1A1A1A", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", marginBottom: 6 }}>{pt.emoji} {pt.label}{pt.disabled ? " — Coming soon" : ""}</div>
                <div style={{ fontSize: 12, color: "#888", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", lineHeight: 1.5 }}>{pt.desc}</div>
              </div>
            ))}
          </div>

          <div style={{ fontSize: 11, color: "#999", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 12, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>Advanced Campaigns</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 12 }}>
            {campaignOverrides.map(pt => (
              <div key={pt.id} onClick={() => { setPlugType(pt.id); setMode("campaign"); setStep(2); }} style={{ background: CARD, border: `1px solid ${B}`, borderRadius: 10, padding: "16px 18px", cursor: "pointer", transition: "all 0.15s" }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: "#1A1A1A", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", marginBottom: 6 }}>{pt.emoji} {pt.label}</div>
                <div style={{ fontSize: 12, color: "#888", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", lineHeight: 1.5 }}>{pt.desc}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ── PLUG HISTORY ── */}
        {history.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 11, color: "#999", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 14, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>Recent plugs</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {history.filter(p => p.status !== "deleted").map(p => (
                <div key={p.id} style={{ background: CARD, border: `1px solid ${B}`, borderRadius: 10, padding: "14px 18px", display: "flex", alignItems: "flex-start", gap: 14 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 11, background: "#EBF5FB", color: G, borderRadius: 4, padding: "2px 8px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontWeight: 600 }}>{PLUG_TYPES.find(t => t.id === p.plug_type)?.label || p.plug_type}</span>
                      <span style={{ fontSize: 11, borderRadius: 4, padding: "2px 8px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontWeight: 600, background: STATUS_COLORS[p.status] + "20", color: STATUS_COLORS[p.status] }}>{(p.status || "").toUpperCase()}</span>
                    </div>
                    <div style={{ fontSize: 13, color: "#444", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {(p.generated_caption || "").slice(0, 120)}{(p.generated_caption || "").length > 120 ? "\u2026" : ""}
                    </div>
                    {p.scheduled_time && <div style={{ fontSize: 11, color: "#888", marginTop: 4, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>Scheduled: {new Date(p.scheduled_time).toLocaleString()}</div>}
                  </div>
                  <div style={{ display: "flex", gap: 6, flexShrink: 0, alignItems: "center" }}>
                    {p.analytics_url && (
                      <a href={p.analytics_url} target="_blank" rel="noreferrer" title="View impressions, clicks and engagement on LinkedIn" style={{ fontSize: 12, color: "#0077B5", textDecoration: "none", whiteSpace: "nowrap", flexShrink: 0 }} onMouseEnter={e => e.target.style.textDecoration = "underline"} onMouseLeave={e => e.target.style.textDecoration = "none"}>View analytics →</a>
                    )}
                    <button onClick={() => { setCurrentPlugId(p.id); setCaption(p.generated_caption || ""); setPlugType(p.plug_type || "shameless"); setOffering(p.offering || ""); setOfferingLink(p.offering_link || ""); setMode(p.plug_type === "shameless" ? "evergreen" : "campaign"); setStep(5); window.scrollTo({ top: 0, behavior: "smooth" }); }} style={{ background: G, color: "#fff", border: "none", borderRadius: 6, padding: "5px 12px", fontSize: 12, cursor: "pointer", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontWeight: 600 }}>Edit</button>
                    {p.status === "muted"
                      ? <button onClick={async () => { await fetch(`${API_BASE}/app/api/user/plug/${p.id}/unmute`, { method: "PUT", credentials: "include" }); setHistory(h => h.map(x => x.id === p.id ? { ...x, status: "draft" } : x)); }} style={{ background: "transparent", color: G, border: `1px solid ${G}`, borderRadius: 6, padding: "5px 12px", fontSize: 12, cursor: "pointer", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>Unmute</button>
                      : <button onClick={() => handleMute(p.id)} style={{ background: "transparent", color: "#888", border: `1px solid ${B}`, borderRadius: 6, padding: "5px 12px", fontSize: 12, cursor: "pointer", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>Mute</button>
                    }
                    <button onClick={() => handleDelete(p.id)} style={{ background: "transparent", color: "#dc3545", border: "1px solid #FECDD3", borderRadius: 6, padding: "5px 12px", fontSize: 12, cursor: "pointer", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>Delete</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════
  // WIZARD: Evergreen or Campaign flow
  // ══════════════════════════════════════════════════════════════
  const isEvergreen = mode === "evergreen";
  const wizardLabel = isEvergreen ? "Evergreen Plug" : (PLUG_TYPES.find(t => t.id === plugType)?.label || "Campaign");
  const wizardSteps = plugType === "custom" ? ["Write","Preview & Post"] : ["Tone","Your Q&A","Generate","Preview & Post"];
  const wizardOffset = plugType === "custom" ? 2 : 1; // custom starts at step 3 in old numbering

  return (
    <div>
      {/* Header with back button */}
      <div style={{ marginBottom: 24 }}>
        <button onClick={handleBackToHome} style={{ background: "transparent", color: "#888", border: "none", padding: 0, fontSize: 13, cursor: "pointer", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", marginBottom: 12 }}>← Back to My Plug</button>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
          <div style={{ fontFamily: "'Playfair Display',Georgia,serif", fontSize: 26 }}>{isEvergreen ? "Evergreen Plug" : "Campaign"}</div>
          {isEvergreen
            ? <span style={{ background: GRN + "20", color: GRN, border: `1px solid ${GRN}40`, borderRadius: 6, fontSize: 11, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontWeight: 700, padding: "3px 10px", letterSpacing: "0.08em" }}>ALWAYS ON</span>
            : <span style={{ background: "#FEF3C7", color: "#92400E", border: "1px solid #FCD34D", borderRadius: 6, fontSize: 11, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontWeight: 700, padding: "3px 10px", letterSpacing: "0.08em" }}>AUTHORITY EXCLUSIVE</span>
          }
        </div>
        <div style={{ fontSize: 15, color: "#666", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
          {isEvergreen
            ? "Your weekly plug post — runs automatically every week."
            : `Replace your evergreen plug for selected week(s) with a ${wizardLabel} post.`
          }
        </div>
      </div>

      {/* Step indicator */}
      <div style={{ display: "flex", alignItems: "center", gap: 0, marginBottom: 28, overflowX: "auto" }}>
        {wizardSteps.map((s, i) => {
          const n = i + 2; // wizard steps start at 2 (tone) in internal numbering
          const realStep = plugType === "custom" ? n + 1 : n; // custom skips to step 3
          const done = step > realStep;
          const active = step === realStep;
          return (
            <React.Fragment key={n}>
              <div
                onClick={() => { if (step > realStep) setStep(realStep); }}
                style={{ display: "flex", alignItems: "center", gap: 7, cursor: step > realStep ? "pointer" : "default", opacity: active ? 1 : done ? 0.7 : 0.35 }}
              >
                <div style={{ width: 24, height: 24, borderRadius: "50%", background: done ? GRN : active ? G : "#E0E0E0", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, flexShrink: 0 }}>{done ? "\u2713" : i + 1}</div>
                <span style={{ fontSize: 12, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontWeight: active ? 600 : 400, color: active ? "#1A1A1A" : "#888", whiteSpace: "nowrap" }}>{s}</span>
              </div>
              {i < wizardSteps.length - 1 && <div style={{ flex: 1, height: 1, background: "#E0E0E0", minWidth: 12, margin: "0 6px" }} />}
            </React.Fragment>
          );
        })}
      </div>

      {/* ── Step 2: Choose tone ── */}
      {step === 2 && (
        <div>
          <div style={{ fontSize: 11, color: "#999", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 16, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>Choose your tone</div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 28 }}>
            {PLUG_TONES.map(t => (
              <button key={t} onClick={() => setTone(t)} style={{ padding: "10px 20px", borderRadius: 24, border: `2px solid ${tone === t ? GRN : B}`, background: tone === t ? GRN + "15" : CARD, color: tone === t ? GRN : "#666", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 14, fontWeight: tone === t ? 600 : 400, cursor: "pointer", transition: "all 0.15s" }}>{t}</button>
            ))}
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={handleBackToHome} style={{ background: "transparent", color: "#888", border: `1px solid ${B}`, borderRadius: 10, padding: "12px 22px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 14, cursor: "pointer" }}>← Back</button>
            <button onClick={() => setStep(3)} style={{ background: G, color: "#fff", border: "none", borderRadius: 10, padding: "12px 28px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 15, fontWeight: 600, cursor: "pointer" }}>Next: Your Q&A →</button>
          </div>
        </div>
      )}

      {/* ── Step 3: Q&A (or freeform for custom) ── */}
      {step === 3 && plugType !== "custom" && (
        <div>
          <div style={{ fontSize: 11, color: "#999", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 16, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>Answer these quickly</div>

          {/* Voice direction toggle */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 20, background: "#F9FAFB", border: `1px solid ${B}`, borderRadius: 10, padding: "12px 16px" }}>
            <span style={{ fontSize: 13, color: "#666", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", marginRight: 4 }}>Voice:</span>
            {[["staff", "Staff", "Speak as the company — \"We build...\""], ["ambassador", "Ambassador", "Speak about the company — \"I work with...\""]].map(([val, label, hint]) => (
              <button key={val} onClick={() => setVoiceDirection(val)} title={hint}
                style={{ padding: "6px 16px", borderRadius: 20, border: `1px solid ${voiceDirection === val ? G : B}`, background: voiceDirection === val ? G + "15" : "#fff", color: voiceDirection === val ? G : "#888", fontSize: 13, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontWeight: voiceDirection === val ? 600 : 400, cursor: "pointer", transition: "all 0.15s" }}>{label}</button>
            ))}
          </div>

          {/* Offering field with improve */}
          <div style={{ marginBottom: 18 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: "#444", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>What do you offer?</label>
              {offering.trim().length >= 10 && !suggestions.offering && (
                <button onClick={() => handleImprove("offering", "What do you offer?", offering)} disabled={improving.offering}
                  style={{ background: "transparent", color: G, border: `1px solid ${G}40`, borderRadius: 6, padding: "3px 10px", fontSize: 11, fontWeight: 600, cursor: improving.offering ? "wait" : "pointer", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
                  {improving.offering ? "Improving..." : "Improve with AI"}
                </button>
              )}
            </div>
            <input value={offering} onChange={e => { setOffering(e.target.value); setSuggestions(s => { const n = { ...s }; delete n.offering; return n; }); }} placeholder="e.g. I help lawyers automate their LinkedIn presence"
              style={{ width: "100%", border: `1px solid ${B}`, borderRadius: 8, padding: "10px 14px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 14, outline: "none", boxSizing: "border-box" }} />
            {suggestions.offering && (
              <div style={{ background: "#F0FAF4", border: `1px solid #B7E4C7`, borderRadius: 8, padding: "10px 14px", marginTop: 8 }}>
                <div style={{ fontSize: 11, color: "#1D9E75", fontWeight: 600, marginBottom: 4, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>AI suggestion</div>
                <div style={{ fontSize: 14, color: "#1A1A1A", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", marginBottom: 8 }}>{suggestions.offering}</div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={() => acceptSuggestion("offering", null)} style={{ background: GRN, color: "#fff", border: "none", borderRadius: 6, padding: "5px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>Use this</button>
                  <button onClick={() => dismissSuggestion("offering")} style={{ background: "transparent", color: "#888", border: `1px solid ${B}`, borderRadius: 6, padding: "5px 14px", fontSize: 12, cursor: "pointer", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>Keep mine</button>
                </div>
              </div>
            )}
          </div>

          {/* Dynamic Q&A fields with improve */}
          {questions.map((q, i) => {
            const key = `q${i}`;
            return (
              <div key={i} style={{ marginBottom: 16 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                  <label style={{ fontSize: 13, fontWeight: 600, color: "#444", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>{q}</label>
                  {(answers[q] || "").trim().length >= 10 && !suggestions[key] && (
                    <button onClick={() => handleImprove(key, q, answers[q])} disabled={improving[key]}
                      style={{ background: "transparent", color: G, border: `1px solid ${G}40`, borderRadius: 6, padding: "3px 10px", fontSize: 11, fontWeight: 600, cursor: improving[key] ? "wait" : "pointer", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
                      {improving[key] ? "Improving..." : "Improve with AI"}
                    </button>
                  )}
                </div>
                <textarea value={answers[q] || ""} onChange={e => { setAnswers(a => ({ ...a, [q]: e.target.value })); setSuggestions(s => { const n = { ...s }; delete n[key]; return n; }); }} maxLength={200} rows={2}
                  style={{ width: "100%", border: `1px solid ${B}`, borderRadius: 8, padding: "10px 14px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 14, resize: "vertical", outline: "none", boxSizing: "border-box" }} />
                {suggestions[key] && (
                  <div style={{ background: "#F0FAF4", border: `1px solid #B7E4C7`, borderRadius: 8, padding: "10px 14px", marginTop: 8 }}>
                    <div style={{ fontSize: 11, color: "#1D9E75", fontWeight: 600, marginBottom: 4, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>AI suggestion</div>
                    <div style={{ fontSize: 14, color: "#1A1A1A", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", marginBottom: 8 }}>{suggestions[key]}</div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button onClick={() => acceptSuggestion(key, q)} style={{ background: GRN, color: "#fff", border: "none", borderRadius: 6, padding: "5px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>Use this</button>
                      <button onClick={() => dismissSuggestion(key)} style={{ background: "transparent", color: "#888", border: `1px solid ${B}`, borderRadius: 6, padding: "5px 14px", fontSize: 12, cursor: "pointer", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>Keep mine</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          <div style={{ marginBottom: 24 }}>
            <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#444", marginBottom: 6, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>Add a link <span style={{ fontWeight: 400, color: "#999" }}>(optional)</span></label>
            <input value={offeringLink} onChange={e => setOfferingLink(e.target.value)} placeholder="https://yoursite.com"
              style={{ width: "100%", border: `1px solid ${B}`, borderRadius: 8, padding: "10px 14px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 14, outline: "none", boxSizing: "border-box" }} />
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={() => setStep(2)} style={{ background: "transparent", color: "#888", border: `1px solid ${B}`, borderRadius: 10, padding: "12px 22px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 14, cursor: "pointer" }}>← Back</button>
            <button onClick={() => { setStep(4); handleGenerate(); }} disabled={!offering.trim()} style={{ background: offering.trim() ? G : "#ccc", color: "#fff", border: "none", borderRadius: 10, padding: "12px 28px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 15, fontWeight: 600, cursor: offering.trim() ? "pointer" : "not-allowed" }}>Generate my plug post →</button>
          </div>
        </div>
      )}

      {/* ── Step 3 (Custom): Write your own plug ── */}
      {step === 3 && plugType === "custom" && (
        <div>
          <div style={{ fontSize: 11, color: "#999", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 16, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>Write your plug post</div>
          <div style={{ fontSize: 13, color: "#666", marginBottom: 16, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>Write exactly what you want to post. This goes out as-is — no AI rewrite.</div>
          <textarea value={caption} onChange={e => setCaption(e.target.value)} maxLength={3000} rows={8} placeholder="Write your plug post here..."
            style={{ width: "100%", border: `1px solid ${B}`, borderRadius: 8, padding: "14px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 14, resize: "vertical", outline: "none", boxSizing: "border-box", marginBottom: 16 }} />
          <div style={{ marginBottom: 24 }}>
            <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#444", marginBottom: 6, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>Add a link <span style={{ fontWeight: 400, color: "#999" }}>(optional)</span></label>
            <input value={offeringLink} onChange={e => setOfferingLink(e.target.value)} placeholder="https://yoursite.com"
              style={{ width: "100%", border: `1px solid ${B}`, borderRadius: 8, padding: "10px 14px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 14, outline: "none", boxSizing: "border-box" }} />
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={handleBackToHome} style={{ background: "transparent", color: "#888", border: `1px solid ${B}`, borderRadius: 10, padding: "12px 22px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 14, cursor: "pointer" }}>← Back</button>
            <button onClick={async () => {
              if (!caption.trim()) return;
              setGenerating(true);
              try {
                const cr = await fetch(`${API_BASE}/app/api/user/plug/create`, {
                  method: "POST", credentials: "include",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ plug_type: "custom", tone: "custom", offering: caption.trim(), offering_link: offeringLink || null, answers: {}, generated_caption: caption.trim() }),
                });
                const cd = await cr.json();
                if (cd.plug_post) setCurrentPlugId(cd.plug_post.id);
                setStep(5);
              } catch { setActionMsg("Failed to save plug post."); }
              setGenerating(false);
            }} disabled={!caption.trim() || generating} style={{ background: caption.trim() ? G : "#ccc", color: "#fff", border: "none", borderRadius: 10, padding: "12px 28px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 15, fontWeight: 600, cursor: caption.trim() ? "pointer" : "not-allowed" }}>{generating ? "Saving..." : "Save & preview →"}</button>
          </div>
        </div>
      )}

      {/* ── Step 4: Generate ── */}
      {step === 4 && (
        <div style={{ textAlign: "center", padding: "40px 24px" }}>
          {generating ? (
            <>
              <div style={{ fontSize: 36, marginBottom: 16 }}>&#128029;</div>
              <div style={{ fontFamily: "'Playfair Display',Georgia,serif", fontSize: 20, marginBottom: 8 }}>Your worker bee is writing your plug...</div>
              <div style={{ fontSize: 13, color: "#999", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>This takes about 5 seconds.</div>
            </>
          ) : (
            <>
              <div style={{ fontSize: 36, marginBottom: 16 }}>&#9997;&#65039;</div>
              <div style={{ fontFamily: "'Playfair Display',Georgia,serif", fontSize: 20, marginBottom: 24 }}>Ready to generate your <em style={{ color: GRN }}>{wizardLabel}</em></div>
              <button onClick={handleGenerate} style={{ background: G, color: "#fff", border: "none", borderRadius: 10, padding: "14px 32px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 16, fontWeight: 600, cursor: "pointer" }}>Generate now →</button>
            </>
          )}
          {!generating && <button onClick={() => setStep(3)} style={{ display: "block", margin: "16px auto 0", background: "transparent", color: "#888", border: "none", fontSize: 13, cursor: "pointer", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>← Back to Q&A</button>}
        </div>
      )}

      {/* ── Step 5: Preview & actions ── */}
      {step === 5 && (
        <div>
          <div style={{ marginBottom: 20 }}>
            <LinkedInPreviewCard
              caption={caption}
              articleUrl={null}
              userName={user?.linkedin_name}
              userHeadline={user?.headline}
              userAvatar={user?.linkedin_profile_image}
            />
          </div>

          <div style={{ background: CARD, border: `1px solid ${B}`, borderRadius: 12, padding: "16px 20px", marginBottom: 20, maxWidth: 560 }}>
            <div style={{ fontSize: 12, color: "#888", marginBottom: 8, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>Edit caption</div>
            <textarea value={caption} onChange={e => setCaption(e.target.value)} rows={8}
              style={{ width: "100%", border: `1px solid ${B}`, borderRadius: 8, padding: "12px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 14, lineHeight: 1.65, resize: "vertical", outline: "none", boxSizing: "border-box", color: "#1A1A1A" }} />
            {offeringLink && (
              <div style={{ border: `1px solid ${B}`, borderRadius: 6, padding: "10px 14px", marginTop: 10, fontSize: 12, color: G, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>&#128279; {offeringLink}</div>
            )}
          </div>

          <ComplianceChecker
            caption={caption}
            plugPostId={currentPlugId}
            API_BASE={API_BASE}
            onCaptionFixed={setCaption}
            onResult={setComplianceResult}
          />

          {scheduling && !showConfirmation && (
            <div style={{ background: "#F0FAF4", border: `1px solid #B7E4C7`, borderRadius: 10, padding: "16px 20px", marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
                <input type="datetime-local" value={scheduleTime} onChange={e => setScheduleTime(e.target.value)}
                  style={{ flex: 1, border: `1px solid ${B}`, borderRadius: 8, padding: "9px 12px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 14, outline: "none" }} />
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
                {[["once", "One-time"], ["weekly", "Weekly"], ["monthly", "Monthly"]].map(([val, label]) => (
                  <button key={val} onClick={() => setRepeatMode(val)}
                    style={{ padding: "6px 14px", borderRadius: 20, border: `1px solid ${repeatMode === val ? GRN : B}`, background: repeatMode === val ? GRN + "15" : "#fff", color: repeatMode === val ? GRN : "#666", fontSize: 13, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontWeight: repeatMode === val ? 600 : 400, cursor: "pointer" }}>{label}</button>
                ))}
              </div>
              {repeatMode !== "once" && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 13, color: "#444" }}>
                  <span>Repeat for</span>
                  <input type="number" min={2} max={26} value={repeatCount} onChange={e => setRepeatCount(Math.max(2, Math.min(26, parseInt(e.target.value) || 2)))}
                    style={{ width: 50, border: `1px solid ${B}`, borderRadius: 6, padding: "5px 8px", fontSize: 13, textAlign: "center", outline: "none" }} />
                  <span>{repeatMode === "weekly" ? "weeks" : "months"}</span>
                </div>
              )}
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={() => { if (scheduleTime) setShowConfirmation(true); }} style={{ background: GRN, color: "#fff", border: "none", borderRadius: 8, padding: "10px 20px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
                  {repeatMode === "once" ? "Confirm →" : `Schedule ${repeatCount} posts →`}
                </button>
                <button onClick={() => { setScheduling(false); setRepeatMode("once"); }} style={{ background: "transparent", color: "#888", border: "none", fontSize: 13, cursor: "pointer", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>Cancel</button>
              </div>
            </div>
          )}

          {showConfirmation && (
            <div style={{ marginBottom: 16 }}>
              <OverrideConfirmation
                overrides={[
                  ...getRepeatDates().map(d => ({ weekStart: d.toISOString(), type: plugType, label: (PLUG_TYPES.find(t => t.id === plugType)?.label || plugType), isNew: true })),
                  ...history.filter(p => p.status === "scheduled" && p.scheduled_time && new Date(p.scheduled_time) > new Date()).map(p => ({
                    weekStart: p.scheduled_time,
                    type: p.plug_type,
                    label: (PLUG_TYPES.find(t => t.id === p.plug_type)?.label || p.plug_type),
                  })),
                ]}
                onConfirm={() => { setShowConfirmation(false); handleSchedule(); }}
                onEdit={() => setShowConfirmation(false)}
                onCancel={() => { setShowConfirmation(false); setScheduling(false); setRepeatMode("once"); }}
              />
            </div>
          )}

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
            <button onClick={() => setScheduling(true)} disabled={complianceResult?.has_blocks} style={{ background: complianceResult?.has_blocks ? "#ccc" : G, color: "#fff", border: "none", borderRadius: 10, padding: "11px 22px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 14, fontWeight: 600, cursor: complianceResult?.has_blocks ? "not-allowed" : "pointer" }}>Schedule this</button>
            <button onClick={handlePostNow} disabled={complianceResult?.has_blocks} style={{ background: complianceResult?.has_blocks ? "#ccc" : GRN, color: "#fff", border: "none", borderRadius: 10, padding: "11px 22px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 14, fontWeight: 600, cursor: complianceResult?.has_blocks ? "not-allowed" : "pointer" }}>Post now</button>
            <button onClick={handleSaveDraft} style={{ background: "transparent", color: G, border: `1px solid ${G}`, borderRadius: 10, padding: "10px 22px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 14, cursor: "pointer" }}>Save as draft</button>
            <button onClick={() => handleMute(currentPlugId)} style={{ background: "transparent", color: "#888", border: `1px solid ${B}`, borderRadius: 10, padding: "10px 22px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 14, cursor: "pointer" }}>Mute this week</button>
            <button onClick={() => { setCaption(""); setStep(4); handleGenerate(); }} style={{ background: "transparent", color: "#888", border: `1px solid ${B}`, borderRadius: 10, padding: "10px 22px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 14, cursor: "pointer" }}>Regenerate</button>
          </div>

          <div style={{ display: "flex", alignItems: "flex-start", gap: 10, background: "#F9FAFB", border: `1px solid ${B}`, borderRadius: 10, padding: "14px 18px", marginBottom: 12 }}>
            <input type="checkbox" id="save-default" checked={saveDefault} onChange={e => setSaveDefault(e.target.checked)} style={{ marginTop: 2 }} />
            <label htmlFor="save-default" style={{ fontSize: 14, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", color: "#444", cursor: "pointer" }}>
              Save these as my default plug settings
              <div style={{ fontSize: 12, color: "#999", marginTop: 3 }}>When auto is on, BrandHives will write and schedule your weekly plug automatically using these settings.</div>
            </label>
          </div>

          {actionMsg && <div style={{ fontSize: 14, color: GRN, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", padding: "8px 0" }}>{"\u2713"} {actionMsg}</div>}
        </div>
      )}
    </div>
  );
}
